import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { analyticsService } from '../services/analyticsService';
import { segmentService } from '../services/segmentService';
import { formatCompactNumber, formatCurrency, formatSignedPercent, formatRelativeTime } from '../utils/format';
import Skeleton from '../components/ui/Skeleton';
import ProgressBar from '../components/ui/ProgressBar';
import Icon from '../components/ui/Icon';
import DateRangePicker from '../components/ui/DateRangePicker';
import EventBus from '../utils/EventBus';
import styles from './Dashboard.module.css';

/* ── KPI Card ─────────────────────────────────── */
class KPICard extends React.Component {
  render() {
    const { ico, color, value, label, trend, up, sparkData, sparkColor } = this.props;

    const chartData = sparkData.map((v) => ({ v }));

    return (
      <div className={styles.kpiCard}>
        <div className={styles.kpiTop}>
          <div className={styles.kpiIco} style={{ background: color + '18', color }}>
            <Icon name={ico} size={18} />
          </div>
          <div className={[styles.delta, up ? styles.up : styles.down].join(' ')}>
            {up ? '▲' : '▼'} {trend}
          </div>
        </div>
        <div className={styles.kpiVal}>{value}</div>
        <div className={styles.kpiLbl}>{label}</div>
        <div className={styles.kpiSpark}>
          <ResponsiveContainer width="100%" height={36}>
            <AreaChart data={chartData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={'sg-' + label.replace(/\s/g, '')} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={sparkColor} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={sparkColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke={sparkColor}
                strokeWidth={1.5}
                fill={'url(#sg-' + label.replace(/\s/g, '') + ')'}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
}

class KPICardSkeleton extends React.Component {
  render() {
    return (
      <div className={styles.kpiCard}>
        <div className={styles.kpiTop}>
          <Skeleton variant="circle" width={32} height={32} />
          <Skeleton variant="text" width={50} height={14} />
        </div>
        <Skeleton variant="text" width={90} height={28} style={{ marginTop: 12 }} />
        <Skeleton variant="text" width={120} height={12} style={{ marginTop: 8 }} />
        <Skeleton variant="rect" width="100%" height={36} style={{ marginTop: 12, borderRadius: 4 }} />
      </div>
    );
  }
}

/* ── Growth Chart ─────────────────────────────── */
class GrowthChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = { series: 'mau' };
  }

  render() {
    const { series } = this.state;
    const { months, mau, dau, sess } = this.props;
    const seriesMap = {
      mau: { label: 'MAU', data: mau, color: '#B94040' },
      dau: { label: 'DAU', data: dau, color: '#5080B8' },
      sessions: { label: 'Sessions', data: sess, color: '#5A9E78' },
    };
    const s = seriesMap[series];
    const chartData = months.map((m, i) => ({ m, v: s.data[i] }));

    return (
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div>
            <div className={styles.cardTitle}>User Growth</div>
            <div className={styles.cardSub}>MAU · DAU · Sessions — 12 months</div>
          </div>
          <div className={styles.tabGroup}>
            {Object.entries(seriesMap).map(([key, val]) => (
              <button
                key={key}
                className={[styles.tabBtn, series === key ? styles.tabOn : ''].join(' ')}
                onClick={() => this.setState({ series: key })}
              >
                {val.label}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={0.18} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
            <XAxis dataKey="m" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: 'var(--t2)' }}
              itemStyle={{ color: s.color }}
            />
            <Area type="monotone" dataKey="v" stroke={s.color} strokeWidth={2} fill="url(#growthGrad)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }
}

/* ── Funnel ───────────────────────────────────── */
const FUNNEL_COLORS = ['#B94040', '#C09848', '#5080B8', '#7A6EB0'];

class FunnelChart extends React.Component {
  render() {
    const { funnel } = this.props;
    return (
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div>
            <div className={styles.cardTitle}>Acquisition Funnel</div>
            <div className={styles.cardSub}>Install → lead → earning → KYC</div>
          </div>
        </div>
        <div className={styles.funnelWrap}>
          {funnel.map((step, i) => {
            const col = FUNNEL_COLORS[i % FUNNEL_COLORS.length];
            return (
              <div key={i} className={styles.funnelRow}>
                <div className={styles.funnelBar} style={{ width: step.p + '%', background: col + '22', borderLeft: '3px solid ' + col }} />
                <div className={styles.funnelLabel}>{step.l}</div>
                <div className={styles.funnelCount} style={{ color: col }}>{formatCompactNumber(step.c)}</div>
                <div className={styles.funnelPct}>{step.p}%</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

/* ── Revenue Chart ────────────────────────────── */
// Revenue isn't tracked anywhere in the backend yet (no purchase/order-value
// model) — this renders the real (zero) series rather than hiding the widget.
class RevenueChart extends React.Component {
  render() {
    const { months, rev, tgt } = this.props;
    const chartData = months.map((m, i) => ({ m, rev: rev[i], tgt: tgt[i] }));

    return (
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div>
            <div className={styles.cardTitle}>Revenue vs Target</div>
            <div className={styles.cardSub}>Not tracked yet — see note below</div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={168}>
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
            <XAxis dataKey="m" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} labelStyle={{ color: 'var(--t2)' }} />
            <Legend wrapperStyle={{ fontSize: 11, color: 'var(--t3)' }} />
            <Bar dataKey="rev" name="Revenue" fill="#B94040" radius={[3, 3, 0, 0]} />
            <Bar dataKey="tgt" name="Target" fill="var(--s4)" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
}

/* ── Geo List ─────────────────────────────────── */
const TOP_CITIES_LIMIT = 6;

class GeoList extends React.Component {
  render() {
    // analytics/geos returns every city with no cap — same top-N convention
    // as the segment cards on this page (.slice(0, 6) below).
    const geos = this.props.geos.slice(0, TOP_CITIES_LIMIT);
    const max = geos.reduce((m, g) => Math.max(m, g.percent), 1);
    return (
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <div>
            <div className={styles.cardTitle}>Top Cities</div>
            <div className={styles.cardSub}>Users by city</div>
          </div>
        </div>
        <div className={styles.geoList}>
          {geos.map((g, i) => (
            <div key={i} className={styles.geoRow}>
              <span className={styles.geoName}>{g.city}</span>
              <div className={styles.geoBarWrap}>
                <ProgressBar value={g.percent} max={max} size="sm" />
              </div>
              <span className={styles.geoCnt}>{formatCompactNumber(g.count)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
}

/* ── Segment Cards ────────────────────────────── */
class SegmentCard extends React.Component {
  render() {
    const { seg } = this.props;
    return (
      <div className={styles.segCard}>
        <div className={styles.segTop}>
          <div className={styles.segName}>{seg.name}</div>
        </div>
        <div className={styles.segCnt}>{formatCompactNumber(seg.user_count)}</div>
        <div className={styles.segDesc}>{seg.description || `${seg.conditions?.length || 0} condition(s)`}</div>
      </div>
    );
  }
}

/* ── Feed ─────────────────────────────────────── */
class FeedItem extends React.Component {
  render() {
    const { item } = this.props;
    const pct = item.sentCount ? Math.round((item.deliveredCount / item.sentCount) * 100) : 0;
    return (
      <div className={styles.feedItem}>
        <div className={styles.feedIco} style={{ background: 'rgba(184,66,66,.08)' }}>
          <Icon name="notifications" size={16} />
        </div>
        <div className={styles.feedText}>
          <b>{item.name}</b> ({item.channel}) — {item.status}, {formatCompactNumber(item.sentCount)} sent
          {item.sentCount ? `, ${pct}% delivered` : ''}
        </div>
        <div className={styles.feedTime}>{formatRelativeTime(item.timestamp)}</div>
      </div>
    );
  }
}

class Dashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, dashboard: null, kpis: null, geos: [], feed: [], segments: [] };
    this.handleRangeChange = this.handleRangeChange.bind(this);
  }

  componentDidMount() {
    // KPIs/feed/segments aren't range-scoped (see analyticsService.getDashboard's
    // comment) — loaded once here, independent of the date range picker below.
    Promise.all([
      analyticsService.getKPIs(),
      analyticsService.getFeed(),
      segmentService.getAll(),
    ]).then(([kpis, feed, segments]) => {
      this.setState({ kpis, feed, segments });
    }).catch(() => {});
  }

  // DateRangePicker calls this once on its own mount with the default range,
  // then again on every change — this is the only trigger for loading
  // dashboard/geos (both range-scoped).
  handleRangeChange(range) {
    this.setState({ loading: true });
    Promise.all([
      analyticsService.getDashboard(range),
      analyticsService.getGeos(range),
    ]).then(([dashboard, geos]) => {
      this.setState({ loading: false, dashboard, geos });
    }).catch(() => this.setState({ loading: false }));
  }

  render() {
    const { loading, dashboard, kpis, geos, feed, segments } = this.state;

    // DateRangePicker must render unconditionally, even while loading — its
    // own componentDidMount is what fires the first handleRangeChange (it
    // owns the default range), so gating it behind `!dashboard` would
    // deadlock: nothing would ever trigger the initial load.
    if (loading || !dashboard || !kpis) {
      return (
        <>
          <div className={styles.pageHead}>
            <DateRangePicker onChange={this.handleRangeChange} />
          </div>
          <div className={styles.g4}>
            {[0, 1, 2, 3].map((i) => <KPICardSkeleton key={i} />)}
          </div>
        </>
      );
    }

    const kpiCards = [
      { ico: 'group', color: '#B94040', sparkColor: '#B94040', value: formatCompactNumber(kpis.mau.value), label: 'Monthly Active Users', trend: formatSignedPercent(kpis.mau.delta), up: kpis.mau.up, sparkData: dashboard.MAU },
      { ico: 'bolt', color: '#5080B8', sparkColor: '#5080B8', value: formatCompactNumber(kpis.session.value), label: 'Sessions (proxy: HOME event)', trend: formatSignedPercent(kpis.session.delta), up: kpis.session.up, sparkData: dashboard.SESS_SPARK },
      { ico: 'loop', color: '#5A9E78', sparkColor: '#5A9E78', value: kpis.retention.value + '%', label: 'Week-over-week Retention', trend: formatSignedPercent(kpis.retention.delta), up: kpis.retention.up, sparkData: dashboard.RET_SPARK },
      { ico: 'payments', color: '#A8883A', sparkColor: '#A8883A', value: formatCurrency(kpis.revenue.value), label: 'Revenue (not tracked)', trend: formatSignedPercent(kpis.revenue.delta), up: kpis.revenue.up, sparkData: dashboard.REV },
    ];

    return (
      <>
        <div className={styles.pageHead}>
          <DateRangePicker onChange={this.handleRangeChange} />
        </div>

        <div className={styles.g4}>
          {kpiCards.map((card) => <KPICard key={card.label} {...card} />)}
        </div>

        <div className={styles.g64}>
          <GrowthChart months={dashboard.MONTHS} mau={dashboard.MAU} dau={dashboard.DAU} sess={dashboard.SESS} />
          <FunnelChart funnel={dashboard.FUNNEL} />
        </div>

        <div className={styles.sectionHead}>
          <div>
            <div className={styles.sectionTitle}>User Segments</div>
            <div className={styles.sectionSub}>Saved audience segments</div>
          </div>
          <button
            className={styles.newSegBtn}
            onClick={() => EventBus.emit('openWizard', { step: 'segment' })}
          >
            + New Segment
          </button>
        </div>
        <div className={styles.g3}>
          {segments.slice(0, 6).map((seg) => <SegmentCard key={seg.id} seg={seg} />)}
        </div>

        <div className={styles.g2}>
          <RevenueChart months={dashboard.MONTHS} rev={dashboard.REV} tgt={dashboard.TGT} />
          <GeoList geos={geos} />
        </div>

        <div className={[styles.card, styles.mt14].join(' ')}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Recent Activity</div>
            <span className={styles.cardSub}>Latest campaign sends</span>
          </div>
          {feed.length === 0
            ? <div className={styles.feedItem}>No campaign activity yet.</div>
            : feed.map((item, i) => <FeedItem key={i} item={item} />)
          }
        </div>
      </>
    );
  }
}

export default Dashboard;
